import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ControlAssociationsEditor,
} from './ControlAssociationsEditor.tsx';
import { controlRiskAssociationsFormSchema } from '../schema.ts';
import type { ControlRiskAssociationsFormValues } from '../schema.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { InlineEditActions } from '../../../shared/common/InlineEditActions.tsx';
import { useControlRiskFilterOptions } from '../../../hooks/controls/useControlRiskFilterOptions.ts';
import { useControlItgcRiskOptions } from '../../../hooks/controls/useControlItgcRiskOptions.ts';
import {
  buildProcessLabelsFromMappings,
  buildProcessOptionsFromMappingRow,
  buildRiskLabelsFromMappings,
  extractControlRiskAssociations,
  patchControlRiskMappings,
  readControlRiskMappings,
  resolveWireProcessId,
  toControlRiskMappingsWire,
  type ControlProcessWireOption,
  type ControlRiskMappingWire,
} from '../../../request/controls/controlRiskMappings.ts';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { soxControlQueryKey } from '../../../hooks/controls/useControls.ts';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import type { OptionLabel } from '../../../types/options.ts';

export type ControlRiskMitigatedSectionHandle = {
  buildWirePayload: () => Promise<ControlRiskMappingWire[]>;
  resetForm: () => void;
};

type ControlRiskMitigatedSectionProps = {
  control: Record<string, unknown>;
  controlUuid: string;
  itgcDomainId?: string;
  disableITGCEdit?: boolean;
  /** When set, edit mode is controlled by the parent (unified control + risk save). */
  isEditing?: boolean;
  /** Hide section-level Edit/Save when the parent provides a single action bar. */
  hideInlineActions?: boolean;
  /** Called after risk mappings are updated from the section's own save path. */
  onRiskMappingsSaved?: (mappings: Array<Record<string, unknown>>) => void;
};

export const ControlRiskMitigatedSection = forwardRef<
  ControlRiskMitigatedSectionHandle,
  ControlRiskMitigatedSectionProps
>(({
  control,
  controlUuid,
  itgcDomainId,
  disableITGCEdit = false,
  isEditing: controlledIsEditing,
  hideInlineActions = false,
  onRiskMappingsSaved,
}, ref) => {
  const queryClient = useQueryClient();
  const { selectedAuditYearId } = useAuditYear();
  const [internalEditing, setInternalEditing] = useState(false);
  const isEditing = controlledIsEditing ?? internalEditing;
  const [riskSearchInput, setRiskSearchInput] = useState('');
  const serverRiskMappings = useMemo(() => readControlRiskMappings(control), [control]);
  const [storedRiskMappings, setStoredRiskMappings] = useState<Array<Record<string, unknown>> | null>(null);
  const [processOptionsByRiskId, setProcessOptionsByRiskId] = useState<
    Record<string, ControlProcessWireOption[]>
  >({});
  const isITGCControl =
    String(control.classification ?? '').trim().toLowerCase() === 'itgc';
  const persistedITGCDomainId = String(
    control.itgc_domain_id ??
      (control.itgc_domain as { id?: string } | undefined)?.id ??
      ''
  ).trim();
  const effectiveITGCDomainId = String(itgcDomainId ?? persistedITGCDomainId).trim();

  useEffect(() => {
    setStoredRiskMappings(null);
    setProcessOptionsByRiskId({});
  }, [serverRiskMappings]);

  const riskMappings = storedRiskMappings ?? serverRiskMappings;
  const initialAssociations = useMemo(() => extractControlRiskAssociations({ risk_mappings: riskMappings }), [riskMappings]);
  const riskLabelsByValue = useMemo(() => buildRiskLabelsFromMappings(riskMappings), [riskMappings]);
  const processLabelsByValue = useMemo(
    () => buildProcessLabelsFromMappings(riskMappings),
    [riskMappings]
  );

  const {
    control: formControl,
    handleSubmit,
    reset,
    getValues,
  } = useForm<ControlRiskAssociationsFormValues>({
    resolver: yupResolver(controlRiskAssociationsFormSchema),
    defaultValues: { riskAssociations: initialAssociations },
  });

  const { fields, append, remove } = useFieldArray({
    control: formControl,
    name: 'riskAssociations',
  });

  useEffect(() => {
    reset({ riskAssociations: initialAssociations });
  }, [initialAssociations, reset]);

  const previousITGCDomainIdRef = React.useRef<string>(effectiveITGCDomainId);
  useEffect(() => {
    if (!isITGCControl) {
      previousITGCDomainIdRef.current = effectiveITGCDomainId;
      return;
    }
    if (!isEditing) {
      previousITGCDomainIdRef.current = effectiveITGCDomainId;
      return;
    }
    if (previousITGCDomainIdRef.current === effectiveITGCDomainId) return;
    previousITGCDomainIdRef.current = effectiveITGCDomainId;
    reset({ riskAssociations: [] });
    setProcessOptionsByRiskId({});
    setRiskSearchInput('');
  }, [effectiveITGCDomainId, isEditing, isITGCControl, reset]);

  const businessRiskOptionsQuery = useControlRiskFilterOptions(
    riskSearchInput,
    isEditing && !isITGCControl,
    selectedAuditYearId,
  );
  const itgcRiskOptionsQuery = useControlItgcRiskOptions(
    effectiveITGCDomainId,
    riskSearchInput,
    isEditing && isITGCControl
  );
  const riskOptionsQuery = isITGCControl ? itgcRiskOptionsQuery : businessRiskOptionsQuery;

  const riskOptions = useMemo(() => {
    const pages = riskOptionsQuery.data?.pages ?? [];
    return pages.flatMap((page) => page.items) as OptionLabel[];
  }, [riskOptionsQuery.data?.pages]);

  const mappingProcessOptions = useMemo(
    () => riskMappings.flatMap((mapping) => buildProcessOptionsFromMappingRow(mapping)),
    [riskMappings]
  );

  const sessionProcessOptions = useMemo(
    () => Object.values(processOptionsByRiskId).flat(),
    [processOptionsByRiskId]
  );

  const wireProcessOptions = useMemo(() => {
    const seen = new Set<string>();
    const merged: ControlProcessWireOption[] = [];
    for (const option of [...mappingProcessOptions, ...sessionProcessOptions]) {
      const value = String(option.value ?? '').trim();
      if (!value || seen.has(value)) continue;
      seen.add(value);
      merged.push(option);
    }
    return merged;
  }, [mappingProcessOptions, sessionProcessOptions]);

  const handleProcessOptionsChange = useCallback(
    (riskId: string, options: ControlProcessWireOption[]) => {
      setProcessOptionsByRiskId((prev) => ({ ...prev, [riskId]: options }));
    },
    []
  );

  const buildWireFromValues = useCallback(
  async (values: ControlRiskAssociationsFormValues): Promise<ControlRiskMappingWire[]> => {
      if (isITGCControl) {
        return (values.riskAssociations ?? [])
          .map((row) => String(row.riskId ?? '').trim())
          .filter(Boolean)
          .map((riskID) => ({ risk_id: riskID, process_ids: [] }));
      }
      const wire = toControlRiskMappingsWire(values.riskAssociations, {
        riskOptions,
        riskMappings,
        processOptions: wireProcessOptions,
      });
      if (wire.length === 0 && values.riskAssociations.length > 0) {
        throw new Error('Each risk mapping requires a valid risk id.');
      }
      const hasUnresolvedProcess = values.riskAssociations.some((row) =>
        (row.mitigatingProcessIds ?? []).some((processId) => {
          const resolved = resolveWireProcessId(processId, {
            riskOptions,
            riskMappings,
            processOptions: wireProcessOptions,
            formRiskId: row.riskId,
          });
          return !resolved;
        })
      );
      if (hasUnresolvedProcess) {
        throw new Error('Each mapped process requires a valid process id.');
      }
      return wire;
    },
    [isITGCControl, riskMappings, riskOptions, wireProcessOptions]
  );

  const validateAndBuildWire = useCallback(async () => {
    const values = getValues();
    if (isITGCControl) {
      return buildWireFromValues(values);
    }
    try {
      await controlRiskAssociationsFormSchema.validate(values, { abortEarly: false });
    } catch {
      throw new Error('Each risk must have at least one mapped process.');
    }
    return buildWireFromValues(values);
  }, [buildWireFromValues, getValues, isITGCControl]);

  const resetRiskForm = useCallback(() => {
    reset({ riskAssociations: initialAssociations });
    setProcessOptionsByRiskId({});
    if (controlledIsEditing === undefined) {
      setInternalEditing(false);
    }
  }, [controlledIsEditing, initialAssociations, reset]);

  useImperativeHandle(
    ref,
    () => ({
      buildWirePayload: validateAndBuildWire,
      resetForm: resetRiskForm,
    }),
    [resetRiskForm, validateAndBuildWire]
  );

  const saveMutation = useMutation({
    mutationFn: async (values: ControlRiskAssociationsFormValues) => {
      const wire = await buildWireFromValues(values);
      return patchControlRiskMappings(controlUuid, wire, selectedAuditYearId ?? undefined);
    },
    onSuccess: async (data) => {
      const fromResponse = readControlRiskMappings(data as Record<string, unknown>);
      if (fromResponse.length > 0) {
        setStoredRiskMappings(fromResponse);
        reset({ riskAssociations: extractControlRiskAssociations({ risk_mappings: fromResponse }) });
        onRiskMappingsSaved?.(fromResponse);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: soxControlQueryKey(controlUuid) }),
        queryClient.invalidateQueries({ queryKey: ['controls', 'risk-with-process-mappings-options'] }),
        queryClient.invalidateQueries({ queryKey: ['controls', 'process-with-risk-mapping-options'] }),
      ]);
      toast.success('Risk mappings updated');
      if (controlledIsEditing === undefined) {
        setInternalEditing(false);
      }
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to update risk mappings')));
    },
  });

  const onSave = handleSubmit(
    (values: ControlRiskAssociationsFormValues) => {
      saveMutation.mutate(values);
    },
    () => {
      toast.error(isITGCControl ? 'Select at least one risk.' : 'Each risk must have at least one mapped process.');
    }
  );

  const loadMoreRisks = useCallback(() => {
    if (riskOptionsQuery.hasNextPage && !riskOptionsQuery.isFetchingNextPage) {
      void riskOptionsQuery.fetchNextPage();
    }
  }, [riskOptionsQuery]);

  const readOnlyView = !isEditing || (disableITGCEdit && isITGCControl);
  const showEmptyState = !isEditing && initialAssociations.length === 0 && fields.length === 0;
  const Wrapper = hideInlineActions ? 'div' : 'form';

  return (
    <Wrapper
      data-testid="risk-mitigated-section"
      {...(hideInlineActions ? {} : { onSubmit: onSave })}
      className="space-y-2"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <label className="block font-medium text-sm text-gray-600">Risk Mitigated</label>
        {!hideInlineActions ? (
          <InlineEditActions
            isEditing={isEditing}
            isSaving={saveMutation.isPending}
            onEdit={() => setInternalEditing(true)}
            onCancel={resetRiskForm}
            saveLabel="Save"
          />
        ) : null}
      </div>

      {showEmptyState ? (
        <p className="text-gray-400 text-sm italic">
          No risks linked to this control in the Risk Control Matrix yet.
        </p>
      ) : (
        <ControlAssociationsEditor
          control={formControl}
          fields={fields}
          append={append}
          remove={remove}
          riskOptions={riskOptions}
          riskLabelsByValue={riskLabelsByValue}
          loadingRisks={riskOptionsQuery.isLoading}
          loadingMoreRisks={riskOptionsQuery.isFetchingNextPage}
          riskSearchInput={riskSearchInput}
          hasNextRiskPage={Boolean(riskOptionsQuery.hasNextPage)}
          onRiskSearchChange={setRiskSearchInput}
          onLoadMoreRisks={loadMoreRisks}
          processLabelsByValue={processLabelsByValue}
          riskMappings={riskMappings}
          readOnly={readOnlyView}
          onProcessOptionsChange={handleProcessOptionsChange}
          hideProcessSection={isITGCControl}
          auditYearId={selectedAuditYearId}
        />
      )}
    </Wrapper>
  );
});

ControlRiskMitigatedSection.displayName = 'ControlRiskMitigatedSection';
